import { useCallback, useRef, useState } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import classNames from 'classnames';

import { Popover } from '@/mastodon/components/popover';
import CancelIcon from '@/material-icons/400-24px/cancel.svg?react';
import MovieIcon from '@/material-icons/400-24px/movie.svg?react';
import RefreshIcon from '@/material-icons/400-24px/refresh.svg?react';
import TagIcon from '@/material-icons/400-24px/tag.svg?react';
import { changeTagset } from 'mastodon/actions/compose';
import type { SelectItem } from 'mastodon/components/dropdown_selector';
import { DropdownSelector } from 'mastodon/components/dropdown_selector';
import { Icon } from 'mastodon/components/icon';
import { useAppDispatch } from 'mastodon/store';

import { localDayKey, programScheduleLabel } from '../util/program_schedule';

const messages = defineMessages({
  empty_short: { id: 'tagset.empty.short', defaultMessage: 'Empty' },
  empty_long: { id: 'tagset.empty.long', defaultMessage: 'Empty tagset' },
  episodes_short: {
    id: 'tagset.episodes.short',
    defaultMessage: 'Other episodes',
  },
  episodes_long: {
    id: 'tagset.episodes.long',
    defaultMessage: 'Episode browser of Mulukhiya',
  },
  reload_short: { id: 'tagset.reload.short', defaultMessage: 'Reload' },
  reload_long: { id: 'tagset.reload.long', defaultMessage: 'Reload episodes' },
  change: { id: 'tagset.change', defaultMessage: 'Change tagset' },
  label: { id: 'tagset.label', defaultMessage: 'Livecure' },
});

// モロヘイヤ（mulukhiya-toot-proxy）の番組表 API が返すエントリ。
interface ProgramEntry {
  enable?: boolean;
  series?: string;
  episode?: string | number;
  episode_suffix?: string;
  subtitle?: string;
  air?: boolean;
  livecure?: boolean;
  minutes?: number;
  extra_tags?: string[];
  // 次回放送日（`YYYY-MM-DD`）と放送開始時刻（`HH:MM`）。どちらもモロヘイヤが
  // 返すが、`next_on` を持たない枠がある（#953）。
  next_on?: string;
  start_time?: string;
}

type ProgramResponse = Record<string, ProgramEntry>;

export const TagsetDropdown: React.FC<{
  disabled?: boolean;
}> = ({ disabled }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<SelectItem[]>([]);
  const activeElementRef = useRef<HTMLElement | null>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  // 選択肢を組み立てたときのローカル日付。「今日」「明日」を含むラベルは
  // 日付をまたぐと嘘になるので、開き直したときの作り直し判定に使う。
  const loadedDayRef = useRef<string | null>(null);

  // モロヘイヤから番組表を取得し、タグセットの選択肢を組み立てる。
  // 取得前に program/update を叩いて番組表を最新化するのは旧実装と同じ挙動。
  const loadOptions = useCallback(async () => {
    const now = new Date();
    loadedDayRef.current = localDayKey(now);

    const items: SelectItem[] = [
      {
        value: 'empty',
        icon: 'cancel',
        iconComponent: CancelIcon,
        text: intl.formatMessage(messages.empty_short),
        meta: intl.formatMessage(messages.empty_long),
      },
    ];

    try {
      await fetch('/mulukhiya/api/program/update', { method: 'POST' });
      const response = await fetch('/mulukhiya/api/program');
      const result = (await response.json()) as ProgramResponse;

      for (const [key, entry] of Object.entries(result)) {
        if (!entry.enable) continue;

        const meta: string[] = [];
        // 放送日時を meta の先頭に置く（#953）。API のレスポンスは既に放送順
        // なので、日付が出れば「どれが今日の枠か」が一覧で分かる。
        // `next_on` を持たない枠では空になるので、その場合は足さない。
        const schedule = programScheduleLabel(
          entry.next_on,
          entry.start_time,
          now,
        );
        if (schedule) meta.push(schedule);
        if (entry.episode)
          meta.push(`${entry.episode}${entry.episode_suffix ?? '話'}`);
        if (entry.subtitle) meta.push(`「${entry.subtitle}」`);
        if (entry.air) meta.push('エア番組');
        if (entry.livecure) meta.push('実況');
        if (entry.minutes) meta.push(`(${entry.minutes}分)`);
        for (const tag of entry.extra_tags ?? []) meta.push(tag);

        items.push({
          value: key,
          icon: 'tag',
          iconComponent: TagIcon,
          text: `「${entry.series ?? ''}」用タグセット`,
          meta: meta.join(' '),
        });
      }
    } catch {
      // モロヘイヤ未接続時は番組リストなしで継続する。
    }

    items.push({
      value: 'episodes',
      icon: 'movie',
      iconComponent: MovieIcon,
      text: intl.formatMessage(messages.episodes_short),
      meta: intl.formatMessage(messages.episodes_long),
    });
    items.push({
      value: 'reload',
      icon: 'refresh',
      iconComponent: RefreshIcon,
      text: intl.formatMessage(messages.reload_short),
      meta: intl.formatMessage(messages.reload_long),
    });

    setOptions(items);
  }, [intl]);

  const handleMouseDown = useCallback(() => {
    if (!open && document.activeElement instanceof HTMLElement) {
      activeElementRef.current = document.activeElement;
    }
  }, [open]);

  const handleToggle = useCallback(() => {
    if (open && activeElementRef.current)
      activeElementRef.current.focus({ preventScroll: true });

    // 初回オープン時にモロヘイヤから番組表を取得する（マウントごとの
    // program/update を避けるため effect ではなく開いた時に遅延ロード）。
    //
    // 日付をまたいで開き直したときも取り直す。組み立て済みのラベルは
    // 「今日」「明日」を持つので、日が変わるとそのまま 1 日ズレて嘘になる（#953）。
    if (
      !open &&
      (options.length === 0 || loadedDayRef.current !== localDayKey(new Date()))
    )
      void loadOptions();

    setOpen(!open);
  }, [open, options.length, loadOptions]);

  const handleClose = useCallback(() => {
    if (open && activeElementRef.current)
      activeElementRef.current.focus({ preventScroll: true });

    setOpen(false);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose],
  );

  const handleChange = useCallback(
    (value: string) => {
      if (value === 'reload') {
        void loadOptions();
        return;
      }

      dispatch(changeTagset(value));
    },
    [dispatch, loadOptions],
  );

  return (
    <>
      <button
        type='button'
        ref={setTargetElement}
        title={intl.formatMessage(messages.change)}
        aria-expanded={open}
        onClick={handleToggle}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={classNames('dropdown-button', { active: open })}
      >
        <Icon id='tag' icon={TagIcon} />
        <span className='dropdown-button__label'>
          {intl.formatMessage(messages.label)}
        </span>
      </button>

      <Popover
        isOpen={open}
        onClose={handleClose}
        offset={5}
        reference={targetElement}
      >
        {({ props, placement }) => (
          <div {...props}>
            <div
              className={`dropdown-animation tagset-dropdown__dropdown ${placement}`}
            >
              <DropdownSelector
                items={options}
                value=''
                onClose={handleClose}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </Popover>
    </>
  );
};
